/**
 * @file useChatHistorySteps.ts
 * @description Шаги истории браузера для окна «Сообщения» до 1024
 * (chat-window-design.md §1д, §6): открытие окна и диалога добавляют шаг
 * без смены адреса, системная «назад» идёт диалог → список → окно закрыто.
 *
 * Шаг хранится в `history.state.commoraChat` поверх состояния роутера (его
 * поля сохраняем — иначе роутер потеряет номер записи). Глубина шага — сколько
 * записей окно добавило: «Свернуть» снимает их все одним `history.go`.
 * «Написать клиенту» на телефоне открывает сразу диалог одним шагом —
 * «назад» возвращает на заказ, а не в список.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";

export interface ChatHistoryStep {
  step: "window" | "conversation";
  id: string | null;
  depth: number;
}

/** Что делать, когда браузер перешёл на запись истории. */
export interface ChatHistoryHandlers {
  onClosed: () => void;
  onList: () => void;
  onConversation: (id: string) => void;
}

function currentStep(): ChatHistoryStep | null {
  const state = window.history.state as { commoraChat?: ChatHistoryStep } | null;
  return state?.commoraChat ?? null;
}

function pushStep(step: ChatHistoryStep): void {
  const base = (window.history.state as Record<string, unknown> | null) ?? {};
  window.history.pushState({ ...base, commoraChat: step }, "");
}

function replaceStep(step: ChatHistoryStep | null): void {
  const base = { ...((window.history.state as Record<string, unknown> | null) ?? {}) };
  if (step) base.commoraChat = step;
  else delete base.commoraChat;
  window.history.replaceState(base, "");
}

export function useChatHistorySteps(active: boolean, handlers: ChatHistoryHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!active) return;
    const onPop = () => {
      const step = currentStep();
      if (!step) handlersRef.current.onClosed();
      else if (step.step === "window" || !step.id) handlersRef.current.onList();
      else handlersRef.current.onConversation(step.id);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [active]);

  /** Окно открыто кнопкой — шаг «список». */
  const pushWindow = useCallback(() => {
    if (!active || currentStep()) return;
    pushStep({ step: "window", id: null, depth: 1 });
  }, [active]);

  /** Открыт диалог — шаг «диалог» поверх текущего. */
  const pushConversation = useCallback(
    (id: string) => {
      if (!active) return;
      const now = currentStep();
      if (now?.step === "conversation") replaceStep({ ...now, id });
      else pushStep({ step: "conversation", id, depth: (now?.depth ?? 0) + 1 });
    },
    [active],
  );

  /** «‹» в шапке диалога: к списку. Открыт сразу диалог — шаг заменяем, а не снимаем. */
  const backToList = useCallback((): boolean => {
    if (!active) return false;
    const now = currentStep();
    if (now?.step !== "conversation") return false;
    if (now.depth > 1) {
      window.history.back();
      return true;
    }
    replaceStep({ step: "window", id: null, depth: 1 });
    return false;
  }, [active]);

  /** «Свернуть»: снять все шаги окна. true — закрытие придёт через popstate. */
  const closeSteps = useCallback((): boolean => {
    if (!active) return false;
    const now = currentStep();
    if (!now) return false;
    window.history.go(-now.depth);
    return true;
  }, [active]);

  return useMemo(
    () => ({ pushWindow, pushConversation, backToList, closeSteps }),
    [pushWindow, pushConversation, backToList, closeSteps],
  );
}
